<template>
  <v-menu
    ref="accountMenu"
    location="bottom"
    role="mainmenu"
    open-on-click
  >
    <template #activator="{ props }">
      <StandardButton
        v-bind="props"
        :aria-label="ariaName"
        color="black"
        variant="text"
        rounded
      >
        <div
          v-if="userProfileInitials"
          class="d-flex align-center ga-2"
        >
          <NotificationBadge :notification-count="notificationCount">
            <v-avatar
              color="primary"
              size="36px"
            >
              <ProfilePhoto size="36px" />
            </v-avatar>
          </NotificationBadge>

          <span
            v-if="!showInitials"
            class="profile-name"
          >
            {{ store.state.profile?.name }}
          </span>
        </div>

        <v-icon
          v-else
          icon="menu"
        />
      </StandardButton>
    </template>

    <v-list
      elevation="0"
      border
    >
      <template v-if="!store.state.site.enable_provider_v2">
        <template v-if="showBusinessPages">
          <v-list-item
            :title="$bct('Settings')"
            :to="{ name: 'BusinessShow' }"
            class="fw-500 fs-16 c-black"
            color="primary"
          />

          <v-list-item
            :title="$bct('Staff')"
            :to="{ name: 'ProviderStaffIndex' }"
            class="fw-500 fs-16 c-black"
            color="primary"
          />
        </template>

        <v-list-item
          v-if="false"
          :title="$bct('My profile')"
          :to="{ name: 'Profile' }"
          class="fw-500 fs-16 c-black"
          color="primary"
        />

        <v-list-item
          :title="$bct('Account')"
          :to="{ name: 'ManagerAccount' }"
          class="fw-500 fs-16 c-black"
          color="primary"
        />
      </template>

      <v-list-item
        v-if="store.getters.isEmployed()"
        :to="{ name: 'DocumentIndex' }"
        class="fw-500 fs-16"
        color="primary"
      >
        <template #title>
          <div class="d-flex align-center justify-space-between hover-td-none-deep">
            <span>{{ $bct('My documents') }}</span>

            <NotificationBadge
              :notification-count="notificationItems['agreements']?.length"
              inline
            />
          </div>
        </template>
      </v-list-item>

      <v-list-item
        v-if="store.state.pages.Navigation.links.faq"
        :href="store.state.pages.Navigation.links.faq"
        :title="$bct('FAQ')"
        class="fw-500 fs-16"
        color="primary"
      />

      <v-list-item
        id="menu_terms"
        :title="$bct('Terms of Service')"
        class="fw-500 fs-16"
        href="https://www.getbridgecare.com/terms-of-service"
        target="_blank"
        tracked
      />

      <v-list-item
        id="menu_privacy"
        :title="$bct('Privacy Policy')"
        class="fw-500 fs-16"
        color="primary"
        href="https://www.getbridgecare.com/privacy-policy"
        target="_blank"
        tracked
      />

      <v-list-item
        v-if="customLink1"
        id="link_1_link"
        :href="customLink1['url']"
        :title="$bct(customLink1['name'])"
        color="primary"
        target="_blank"
      />

      <v-list-item
        v-if="customLink2"
        id="link_2_link"
        :href="customLink2['url']"
        :title="$bct(customLink2['name'])"
        color="primary"
        target="_blank"
      />

      <v-list-item
        id="account_menu_signout"
        :title="$bct('Sign out')"
        :to="{ name: 'SignOut' }"
        class="fw-500 fs-16"
        color="primary"
        tracked
      />
    </v-list>
  </v-menu>
</template>

<script setup>
import _ from 'lodash'; // eslint-disable-line id-length
import { useDisplay } from 'vuetify';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';

import { needsMFA } from '@/shared/services/auth';
import NotificationBadge from '@/manager/components/NotificationBadge.vue';
import ProfilePhoto from '@/manager/components/profile/ProfilePhoto.vue';
import { toAcronym } from '@/shared/services/string-utils';
import useApi from '@/shared/composables/useApi';

const $bct = inject('translator');

const { api } = useApi();
const display = useDisplay();
const route = useRoute();
const store = useStore();

const agreements = ref([]);

watch(
  () => store.state.profile,
  async () => {
    await loadAgreements();
  },
);

const ariaName = computed(() => {
  const defaultLabel = $bct('Open account settings menu');
  const profileDetails = showInitials.value
    ? `${$bct('Initials are')} ${userProfileInitials.value}, ${$bct('full name is')} ${store.state.profile?.name}`
    : `${$bct('Name is')} ${store.state.profile?.name}`;
  return `${defaultLabel}, ${profileDetails}`;
});

const agreementNotifications = computed(() => {
  return agreements.value?.filter((agreement) => {
    let orderedSignatures;

    switch (agreement.status) {
      case 'Sent':
        return true;
      case 'Partial':
        orderedSignatures = _.orderBy(agreement.signatures, 'ordinal');

        for (const signature of orderedSignatures) {
          if (signature.signed_at) continue;
          return signature.signatory_id === store.state.profile.id;
        }

        return false;
      default:
        return false;
    }
  });
});

const customLink1 = computed(() => {
  if (!store.state.pages.Navigation) return null;
  if (!store.state.pages.Navigation?.link_1_url) return null;
  if (!store.state.pages.Navigation?.link_1_name) return null;

  return {
    name: store.state.pages.Navigation.link_1_name,
    url: store.state.pages.Navigation.link_1_url,
  };
});

const customLink2 = computed(() => {
  if (!store.state.pages.Navigation) return null;
  if (!store.state.pages.Navigation?.link_2_url) return null;
  if (!store.state.pages.Navigation?.link_2_name) return null;

  return {
    name: store.state.pages.Navigation.link_2_name,
    url: store.state.pages.Navigation.link_2_url,
  };
});

const notificationCount = computed(() => {
  let count = 0;
  if (!store.state?.profile?.id) return count;

  for (const key in notificationItems.value) {
    count += notificationItems.value[key]?.length || 0;
  }
  return count;
});

const notificationItems = computed(() => {
  if (!store.state?.profile?.id) return {};

  return {
    agreements: agreementNotifications.value,
  };
});

const showBusinessPages = computed(() => {
  return store.state.pages.Settings.enabled && store.getters.showBusinessAdminPages();
});

const showInitials = computed(() => display.mdAndDown.value);
const userProfileInitials = computed(() => toAcronym(store.state.profile?.name));

function load() {
  void loadAgreements();
}

async function loadAgreements() {
  if (!store.state?.profile?.id) return;
  if (route.meta?.registration || needsMFA(store)) return;

  const response = await api.manager.agreement.index();
  agreements.value = response?.data;
}

onMounted(load);
</script>
